// SmartDate — drop-in replacement for <input type="date">.
//
// Problem we solve:
//   Native browser date pickers are inconsistent and hostile to historical
//   dates (DOB, expiration windows, etc). On Chrome the year spinner is
//   tiny and unclickable, on Safari/iOS the year field steals focus, and
//   on most Android WebViews you can't type the year at all. Our users
//   regularly enter dates 30-100 years in the past and need to fly.
//
// Solution:
//   Three plain <select> dropdowns (Month / Day / Year). Works identically
//   on every browser/device, no JS magic, no keyboard quirks.
//
// API:
//   <SmartDate
//     value="YYYY-MM-DD"      // ISO string, same as <input type="date">
//     onChange={(e)=>fn(e.target.value)}   // synthetic event for drop-in
//     min="YYYY-MM-DD"        // optional, narrows year range
//     max="YYYY-MM-DD"        // optional, narrows year range
//     yearsBack={100}         // optional shortcut when min not given
//     yearsForward={5}        // optional shortcut when max not given
//     className=""            // applied to wrapping div for spacing only
//     disabled                // disables all 3 selects
//     readOnly                // shows value, no edits
//     data-testid="..."       // a parent test id; sub-selects get -month/-day/-year
//   />

import React from "react";

const MONTHS = [
  { v: "01", lbl: "Jan" },  { v: "02", lbl: "Feb" }, { v: "03", lbl: "Mar" },
  { v: "04", lbl: "Apr" },  { v: "05", lbl: "May" }, { v: "06", lbl: "Jun" },
  { v: "07", lbl: "Jul" },  { v: "08", lbl: "Aug" }, { v: "09", lbl: "Sep" },
  { v: "10", lbl: "Oct" },  { v: "11", lbl: "Nov" }, { v: "12", lbl: "Dec" },
];
const pad2 = (n) => String(n).padStart(2, "0");

const daysInMonth = (year, month) => {
  // Defensive: when year missing, assume a leap year so the day list
  // doesn't shrink mid-pick (re-trimmed when year is set).
  const y = parseInt(year, 10) || 2024;
  const m = parseInt(month, 10) || 1;
  return new Date(y, m, 0).getDate();
};

const isComplete = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || "");

const yearFromIso = (iso, fallback) => {
  if (!iso) return fallback;
  const y = parseInt((iso || "").slice(0, 4), 10);
  return Number.isFinite(y) ? y : fallback;
};

const SmartDate = React.forwardRef(function SmartDate(props, ref) {
  const {
    value,
    onChange,
    min,
    max,
    yearsBack = 110,
    yearsForward = 5,
    className = "",
    disabled = false,
    readOnly = false,
    name,
    id,
    ...rest
  } = props;

  const testid = rest["data-testid"];

  const now = new Date();
  const maxYear = yearFromIso(max, now.getFullYear() + yearsForward);
  const minYear = yearFromIso(min, now.getFullYear() - yearsBack);

  // Local state holds partial selections (year alone, year+month).
  // The parent only sees a complete ISO date or "" — never a malformed
  // "1985--" string that would fail validators and flash red.
  const [local, setLocal] = React.useState(() => {
    if (isComplete(value)) {
      const [yy, mm, dd] = value.split("-");
      return { y: yy, m: mm, d: dd };
    }
    return { y: "", m: "", d: "" };
  });

  // Sync from parent if it forces a value change (e.g. autosave roundtrip
  // or a "Reset" button). We only re-sync when the new value is either
  // a complete ISO date or an explicit blank — partial junk is ignored.
  React.useEffect(() => {
    if (isComplete(value)) {
      const [yy, mm, dd] = value.split("-");
      setLocal((prev) => (prev.y === yy && prev.m === mm && prev.d === dd ? prev : { y: yy, m: mm, d: dd }));
    } else if (!value) {
      setLocal((prev) => (prev.y === "" && prev.m === "" && prev.d === "" ? prev : { y: "", m: "", d: "" }));
    }
  }, [value]);

  const fire = (newValue) => {
    if (!onChange) return;
    // Mimic an <input> change event so callers using onChange={e => f(e.target.value)}
    // continue to work verbatim.
    onChange({ target: { value: newValue, name, id }, currentTarget: { value: newValue, name, id } });
  };

  const commit = (yy, mm, dd) => {
    setLocal({ y: yy, m: mm, d: dd });
    if (yy && mm && dd) fire(`${yy}-${mm}-${dd}`);
    else if (value) fire(""); // Parent had a value, user cleared a piece → clear parent.
  };

  const { y, m, d } = local;
  const daysCount = daysInMonth(y, m);
  // Day count shrinks (Feb 30 → 28/29) if month/year change; trim invalid day.
  const safeDay = d && parseInt(d, 10) > daysCount ? "" : d;

  const baseSel = "h-9 rounded-md text-sm border border-input bg-transparent px-1.5 transition-colors focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

  const years = React.useMemo(() => {
    const lo = Math.min(minYear, maxYear);
    const hi = Math.max(minYear, maxYear);
    const out = [];
    for (let yr = hi; yr >= lo; yr--) out.push(yr);
    return out;
  }, [minYear, maxYear]);

  // IMPORTANT: options are injected as raw HTML (not JSX `.map()`).
  // The preview visual-editor instruments JSX-mapped children by wrapping
  // them in <span data-ve-dynamic style="display:contents">. Native <select>
  // controls ignore <option>s nested inside a <span>, so mapped options become
  // unselectable in preview (the dropdown shows only the placeholder). Building
  // the option markup as a string yields clean, selectable <option> children.
  const monthHtml = React.useMemo(
    () => `<option value="">Month</option>` + MONTHS.map((o) => `<option value="${o.v}">${o.lbl}</option>`).join(""),
    []
  );
  const dayHtml = React.useMemo(() => {
    let s = `<option value="">Day</option>`;
    for (let i = 1; i <= daysCount; i++) s += `<option value="${pad2(i)}">${i}</option>`;
    return s;
  }, [daysCount]);
  const yearHtml = React.useMemo(() => {
    let s = `<option value="">Year</option>`;
    for (const yr of years) s += `<option value="${yr}">${yr}</option>`;
    return s;
  }, [years]);

  return (
    <div
      ref={ref}
      className={`grid grid-cols-[1fr_0.8fr_1.15fr] gap-1.5 ${className}`}
      data-testid={testid}
    >
      <select
        className={baseSel}
        value={m || ""}
        disabled={disabled || readOnly}
        onChange={(e) => commit(y || "", e.target.value, safeDay || "")}
        data-testid={testid ? `${testid}-month` : undefined}
        aria-label="Month"
        dangerouslySetInnerHTML={{ __html: monthHtml }}
      />
      <select
        className={baseSel}
        value={safeDay || ""}
        disabled={disabled || readOnly}
        onChange={(e) => commit(y || "", m || "", e.target.value)}
        data-testid={testid ? `${testid}-day` : undefined}
        aria-label="Day"
        dangerouslySetInnerHTML={{ __html: dayHtml }}
      />
      <select
        className={baseSel}
        value={y || ""}
        disabled={disabled || readOnly}
        onChange={(e) => commit(e.target.value, m || "", safeDay || "")}
        data-testid={testid ? `${testid}-year` : undefined}
        aria-label="Year"
        dangerouslySetInnerHTML={{ __html: yearHtml }}
      />
    </div>
  );
});

export { SmartDate };
export default SmartDate;
